import { useMemo } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { BUTTON_LABELS } from '../../constants/buttonLabels';
import { useAccountEquity } from '../../hooks/useAccountEquity';
import { useMarketOrderPriceImpact } from '../../hooks/useMarketOrderPriceImpact';
import { useModalState } from '../../hooks/useModalState';
import { usePositions } from '../../hooks/usePositions';
import { useProductPricesSub } from '../../hooks/useProductPricesSub';
import { ProductType } from '../../hooks/useProducts';
import { useActiveSelectedSubaccount } from '../../hooks/useSelectedSubaccount';
import { useHasSlTpError } from '../../hooks/useSlTpHooks';
import { useTradeFormAccountSummaryData } from '../../hooks/useTradeFormAccountSummary';
import {
  formatDollar,
  formatLargeDollar,
  formatNumberWithDecimals,
  formatPercentage,
} from '../../lib/formatters';
import { Gwei } from '../../lib/gwei';
import { localStorageKeys } from '../../lib/localstorageKeys';
import { multiply } from '../../lib/math';
import { capitalize } from '../../lib/stringHelpers';
import {
  liquidationPriceTooltip,
  maintenanceMarginTooltip,
  marginUsageTooltip,
} from '../../lib/tooltips';
import { OrderType } from '../../types/orders';
import { Alert } from '../Alert/Alert';
import { Button } from '../catalyst-ui-kit/button';
import { Checkbox, CheckboxField } from '../catalyst-ui-kit/checkbox';
import { Label } from '../catalyst-ui-kit/fieldset';
import { HealthIcon } from '../HealthIcon/HealthIcon';
import { getHealthColorClass } from '../HealthIcon/healthThreshold';
import { ResponsiveModal } from '../ResponsiveModal/ResponsiveModal';
import { Tooltip } from '../Tooltip/Tooltip';
import { useTradeFormAmountError } from '../TradePage/hooks/useTradeFormAmountError';
import {
  useCurrentPosition,
  useNewPositionSize,
  useProjectedLiquidationPrice,
} from '../TradePage/TradeForm/hooks/calculationHooks';
import { TradeFormPrimaryButton } from '../TradePage/TradeForm/TradeFormPrimaryButton';
import { useTradeFormStore } from '../TradePage/TradeForm/TradeFormState';
import { StatRow, StatRowValue, StatRowValueDiff } from '../TradePage/TradeForm/TradeFormStats';

export type ConfirmOrderModalUiProps = {
  isOpen: boolean;
  onClose: () => void;
  marginUsage: bigint | undefined;
  projectedMarginUsage: bigint | undefined;
  maintenanceMargin: bigint | undefined;
  projectedMaintenanceMargin: bigint | undefined;
  price: bigint | undefined;
  liqPriceAfter: bigint | undefined;
  orderType: OrderType;
  product: ProductType;
  amount: bigint | undefined;
  side: 'long' | 'short';
  showConfirmOrderModal: boolean;
  orderValue: bigint;
  setShowConfirmOrderModal: (show: boolean) => void;
  amountErrorMessage: string | undefined;
  tpSlError: boolean;
};

export const ConfirmOrderModalUi: React.FC<ConfirmOrderModalUiProps> = ({
  isOpen,
  onClose,
  marginUsage,
  projectedMarginUsage,
  maintenanceMargin,
  projectedMaintenanceMargin,
  price,
  product,
  liqPriceAfter,
  orderType,
  amount,
  side,
  showConfirmOrderModal,
  setShowConfirmOrderModal,
  orderValue,
  amountErrorMessage,
  tpSlError,
}) => {
  const colorClass = side === 'long' ? 'text-contentGreen' : 'text-contentRed';
  return (
    <ResponsiveModal
      onClose={onClose}
      open={isOpen}
      title={
        <span>
          <span className={colorClass}>
            {capitalize(orderType)} {capitalize(side)}
          </span>{' '}
          {product.displayTicker}
        </span>
      }
    >
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <StatRow label="Order Price" labelClassName="text-xs" childrenWrapperClassName="text-xs">
            {orderType === 'MARKET' ? 'Market' : formatDollar(price)}
          </StatRow>
          <StatRow label="Order Size" labelClassName="text-xs" childrenWrapperClassName="text-xs">
            {formatNumberWithDecimals(amount)} {product.baseTokenName}
          </StatRow>
          <StatRow label="Order Value" labelClassName="text-xs" childrenWrapperClassName="text-xs">
            {formatDollar(orderValue)}
          </StatRow>
          <StatRow
            labelClassName="text-xs"
            childrenWrapperClassName="text-xs"
            label={<Tooltip content={liquidationPriceTooltip()}>Liquidation Price</Tooltip>}
          >
            <StatRowValue
              value={liqPriceAfter}
              formatValue={(x) => `~${formatLargeDollar(x)}`}
              className={liqPriceAfter !== undefined ? 'text-contentOrange' : ''}
            />
          </StatRow>
        </div>
        <div className="flex flex-col gap-2">
          <StatRow
            labelClassName="text-xs"
            childrenWrapperClassName="text-xs"
            label={<Tooltip content={marginUsageTooltip()}>Margin Usage</Tooltip>}
          >
            {marginUsage !== undefined && <HealthIcon marginUsage={Gwei.fromGwei(marginUsage)} />}
            <StatRowValueDiff
              currentValue={marginUsage}
              projectedValue={projectedMarginUsage}
              formatValue={formatPercentage}
              projectedClassName={
                projectedMarginUsage === undefined
                  ? undefined
                  : getHealthColorClass(Gwei.fromGwei(projectedMarginUsage))
              }
              currentClassName={
                marginUsage === undefined
                  ? undefined
                  : getHealthColorClass(Gwei.fromGwei(marginUsage))
              }
            />
          </StatRow>
          <StatRow
            labelClassName="text-xs"
            childrenWrapperClassName="text-xs"
            label={<Tooltip content={maintenanceMarginTooltip()}>Maintenance Margin</Tooltip>}
          >
            <StatRowValueDiff
              currentValue={maintenanceMargin}
              projectedValue={projectedMaintenanceMargin}
              formatValue={formatLargeDollar}
            />
          </StatRow>
        </div>
        <div>
          <CheckboxField>
            <Checkbox
              name={localStorageKeys.showConfirmOrderModal}
              checked={!showConfirmOrderModal}
              onChange={() => setShowConfirmOrderModal(!showConfirmOrderModal)}
              color="inverseSecondary"
            />
            <Label className="!text-3xs text-contentTertiary">
              Do not show this confirmation modal anymore
            </Label>
          </CheckboxField>
        </div>
        <div className="flex flex-col gap-2">
          {amountErrorMessage ? (
            <Alert variant="error">{amountErrorMessage}</Alert>
          ) : tpSlError ? (
            <Alert variant="error">Invalid stop loss or take profit price</Alert>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            <Button color="tertiary" onClick={onClose}>
              {BUTTON_LABELS.action.cancel}
            </Button>

            <TradeFormPrimaryButton
              product={product}
              alternateLabel={BUTTON_LABELS.action.confirmOrder}
            />
          </div>
        </div>
      </div>
    </ResponsiveModal>
  );
};

type ConfirmOrderModalProps = {
  product: ProductType;
};

export const ConfirmOrderModal: React.FC<ConfirmOrderModalProps> = ({ product }) => {
  const [showConfirmOrderModal, setShowConfirmOrderModal] = useLocalStorage(
    localStorageKeys.showConfirmOrderModal,
    true
  );
  const setConfirmOrderModal = useModalState((state) => state.setConfirmOrderModal);
  const confirmOrderModal = useModalState((state) => state.confirmOrderModal);
  const summary = useTradeFormAccountSummaryData(product);
  const selectedSubaccount = useActiveSelectedSubaccount();
  const orderType = useTradeFormStore(({ orderType }) => orderType);
  const limitPrice = useTradeFormStore(({ limitPrice }) => limitPrice);
  const side = useTradeFormStore(({ side }) => side);
  const amount = useTradeFormStore(({ amount }) => amount);
  const postOnly = useTradeFormStore(({ postOnly }) => postOnly);
  const stopLoss = useTradeFormStore((state) => state.stopLoss);
  const takeProfit = useTradeFormStore((state) => state.takeProfit);
  const showTpSl = useTradeFormStore((state) => state.showTpSl);
  const { data: positions } = usePositions({ subaccountId: selectedSubaccount?.id });
  const { executionPrice } = useMarketOrderPriceImpact(product.id, side, amount || 0n);
  const { size: currentPositionSize } = useCurrentPosition(positions, product.id);
  const price = orderType === 'LIMIT' ? limitPrice : executionPrice;
  const newPosSize = useNewPositionSize(currentPositionSize, amount, side);
  const { data: pricesByProductId } = useProductPricesSub();
  const { oraclePrice } = pricesByProductId[product.id] || {};
  const accountEquity = useAccountEquity();
  const liqPriceAfter = useProjectedLiquidationPrice({
    accountEquity,
    orderSize: amount,
    newPositionSize: newPosSize,
    oraclePrice: oraclePrice,
    executionPrice: price,
    feeRate: postOnly ? product.makerFee : product.takerFee,
    maintenanceMarginRatio: product.maintenanceMarginRatio,
    currentPositionSize,
  });

  // Get error states that may have changed since the button was pressed to open
  // modal.
  const { errorMessage: amountErrorMessage } = useTradeFormAmountError(product);
  const { hasStopLossError, hasTakeProfitError } = useHasSlTpError({
    takeProfitPrice: takeProfit,
    stopLossPrice: stopLoss,
    productId: product.id,
    side,
  });
  const tpSlError = showTpSl && (hasStopLossError || hasTakeProfitError);

  const orderValue: bigint = useMemo(() => {
    return price ? multiply(price, amount || 0n) : 0n;
  }, [price, amount]);

  if (!summary.isConnected || !summary.selectedSubaccount) {
    return null;
  }

  return (
    <ConfirmOrderModalUi
      isOpen={confirmOrderModal === 'open'}
      onClose={() => setConfirmOrderModal('closed')}
      marginUsage={summary.marginUsage}
      projectedMarginUsage={summary.projectedMarginUsage}
      maintenanceMargin={summary.maintenanceMargin}
      projectedMaintenanceMargin={summary.projectedMaintenanceMargin}
      price={price}
      liqPriceAfter={liqPriceAfter}
      orderType={orderType}
      amount={amount}
      side={side}
      product={product}
      orderValue={orderValue}
      showConfirmOrderModal={showConfirmOrderModal}
      setShowConfirmOrderModal={setShowConfirmOrderModal}
      amountErrorMessage={amountErrorMessage}
      tpSlError={tpSlError}
    />
  );
};
